import { FC, useEffect } from 'react';
import Typography from '@components/common/typography';
import InfoBlock from '@components/common/info_block';
import Radio from '@components/common/radio';
import { Icon } from '@components/icons';
import { IModalComponentProps } from '@context/modal_context';
import { PermissionLevel, scopesForLevel } from '@api';
import styles from './styles.module.scss';

type ConfigData = {
    isRegenerate?: boolean;
    level?: PermissionLevel;
    scopes?: string[];
};

const OPTIONS: {
    level: PermissionLevel;
    title: string;
    description: string;
    icon: 'key' | 'lock';
}[] = [
    {
        level: 'full',
        title: 'Full access',
        description:
            'Read and write — create payments, send payouts, issue refunds, and manage settings. Recommended for most integrations.',
        icon: 'key'
    },
    {
        level: 'read_only',
        title: 'Read only',
        description:
            'View payments, payouts and reports. Cannot create charges, send payouts, issue refunds, or change settings.',
        icon: 'lock'
    }
];

const CreateApiKeyConfigModal: FC<IModalComponentProps<ConfigData>> = ({
    modalData,
    changeModalData
}) => {
    const level: PermissionLevel = modalData?.level ?? 'full';

    // Seed the default selection so the footer confirm always sends a valid
    // scope set even if the user never touches the picker. (handleCreateApiKey
    // also pre-seeds the level before opening, so this is a defensive fallback.)
    useEffect(() => {
        if (!modalData?.level) {
            changeModalData({ ...modalData, level: 'full', scopes: scopesForLevel('full') });
        }
    }, []);

    const select = (next: PermissionLevel) => {
        changeModalData({ ...modalData, level: next, scopes: scopesForLevel(next) });
    };

    return (
        <div className={styles.wrapper}>
            <Typography className={styles.title} variant="h4">
                {modalData?.isRegenerate ? 'Regenerate API key' : 'Create API key'}
            </Typography>
            <Typography variant="body3" alignment="center" className={styles.subtitle}>
                Choose what this key is allowed to do. You can change it later by regenerating the
                key.
            </Typography>

            <div className={styles.options} role="radiogroup" aria-label="Key permissions">
                {OPTIONS.map((opt) => {
                    const isActive = level === opt.level;
                    return (
                        <div
                            key={opt.level}
                            role="radio"
                            aria-checked={isActive}
                            tabIndex={0}
                            className={[styles.option, isActive ? styles.option_active : ''].join(
                                ' '
                            )}
                            onClick={() => select(opt.level)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    select(opt.level);
                                }
                            }}>
                            <div className={styles.option_icon}>
                                <Icon name={opt.icon} width={18} height={18} />
                            </div>
                            <div className={styles.option_text}>
                                <Typography variant="body3" weight="semibold">
                                    {opt.title}
                                </Typography>
                                <p className={styles.option_desc}>{opt.description}</p>
                            </div>
                            <Radio checked={isActive} />
                        </div>
                    );
                })}
            </div>

            {modalData?.isRegenerate && (
                <InfoBlock variant="warning">
                    Your current key will be permanently deleted. Any API requests or webhooks still
                    using it will stop working — update your integration with the new key.
                </InfoBlock>
            )}
        </div>
    );
};

export default CreateApiKeyConfigModal;
